import TimeSheet from "molecules/TimeSheet";
import LeftIcon from "assets/icons/UI/left.svg";
import RightIcon from "assets/icons/UI/right.svg";
import React, { useState } from "react";
import { Button, DatePicker, Form, Spin, notification } from "antd";
import { ReactComponent as EditIcon } from "assets/icons/UI/edit.svg";
import queryKeys from "common/queryKey";
import { useSelector } from "react-redux";
import { getHigherOfficials, getUserInfo, getUserList } from "redux/selectors";
import { useFetch } from "hooks/useFetch";
import { ClickAwayListener, IconButton } from "@mui/material";
import { useUpdate } from "hooks/useUpdate";
import useWeekData, { periodToDate } from "./useWeekData";
import { useQueryClient } from "react-query";
import { ConfigProvider } from "antd";
import en_GB from "antd/lib/locale-provider/en_GB";
import moment from "moment";
import "moment/locale/en-gb";
import TimeSheetOverView from "./TimeSheetOverView";
import TimeSheetFooterHint from "./TimeSheetFooterHint";
import { DRAFT, PENDING } from ".";
import useTimesheetfns from "./useTimesheetfns";
import { getEmailsByRole } from "utils/functions";

moment.locale("en-gb");

const DeclinedTimeSheet = ({ selectedPeriod }) => {
  const [editable, setEditable] = useState(false);
  const [editables, setEditables] = useState(false);
  const selectedPeriodToDate = periodToDate(selectedPeriod);

  const {
    date,
    week,
    currentWeek,
    edit,
    setEdit,
    currentNiceWeekRange,
    disabledDate,
    handleNextWeek,
    handlePreviousWeek,
    onChange,
    setPersonInCharge,
    personIncharge,
  } = useWeekData(selectedPeriodToDate);

  const { getTimesheet, postTimesheet } = queryKeys;

  const userInfo = useSelector((state) => getUserInfo(state));
  const higherOfficialsList = useSelector((state) => getHigherOfficials(state));
  const CEO = (() => {
      const val = getEmailsByRole(higherOfficialsList, "CEO");
        return Array.isArray(val) ? val : val ? [val] : [];
  })();
  const queryClient = useQueryClient();
  const userList = useSelector(getUserList);

  const currentUser = userList.find((user) => user.userid === userInfo.userid);

  const country = currentUser?.managingoffice?.[0];

  const OfficeSuperVisorId = currentUser?.OfficeSupervisorId || null;

  const currentUserEmployeeId = userList?.find(
    (user) => user?.userid === currentUser?.userid
  )?.employeeid;

  const OfficeSuperVisorUserId = userList?.find(
    (user) =>
      user.employeeid ===
      (currentUser?.userid === CEO
        ? currentUserEmployeeId
        : OfficeSuperVisorId)
  )?.userid;

  const { data: timesheetDataRes, isLoading, isFetched } = useFetch(
    `${getTimesheet.key}-week:${week}-status:declined`,
    `${getTimesheet.url}?week=${week}&employeeId=${userInfo.employer_id}&status=declined`,
    {
      enabled: !!week,
    }
  );

  const [form] = Form.useForm();

  const onSubmit = (
    values,
    draft = false,
    skipActualSummary = false,
    originalTimeSheet
  ) => {
    let timesheet = generateTimeSheetData(
      values,
      (draft = false),
      values,
      true,
      false,
      false,
      true
    );
    if (originalTimeSheet) {
      timesheet = generateTimeSheetData(
        values,
        false,
        originalTimeSheet,
        true,
        true,
        true
      );
    }
    if (!isChargeAbleAmtValid(timesheet)) {
      return false;
    }

   // ✅ Skip Task validation for approved rows only
    const missingTask = timesheet.some((entry) => {
      const status = entry?.status?.toLowerCase?.();
      const isApproved = status === "approved";
      const taskMissing = !entry.task || entry.task.trim() === "";

      // Only flag as missing if NOT approved and no task
      return taskMissing && !isApproved;
    });

    if (missingTask && !draft) {
      notification.warning({
        message: "Task selection required for editable timesheets while submitting",
      });
      return false;
    }

    const isValid = form.validateFields();
    if (!isValid) {
      notification.error({
        message: "Please fill all required fields",
      });
      return false;
    }
    const updatedData = {
      ...timesheetDataRes?.response?.response,
      status: originalTimeSheet ? DRAFT : PENDING,
      timeSheet: timesheet,
    };
    update(updatedData, {
      onSuccess: (res) => {
        // Update local timesheet status manually after success
        form.setFieldsValue({
          ...form.getFieldsValue(),
          status: originalTimeSheet ? DRAFT : PENDING,
        });
        queryClient.invalidateQueries(`${getTimesheet.key}-week:${week}`);
        queryClient.invalidateQueries(
          `${getTimesheet.key}-week:${week}-status:pending`
        );
        queryClient.invalidateQueries(
          `${getTimesheet.key}-week:${week}-status:declined`
        );
        notification.success({
          message:
            draft || originalTimeSheet
              ? "Timesheet Saved Successfully"
              : "Timesheet Submitted Successfully",
        });
      },
    });
  };

  const {
    timeSheet,
    generateTimeSheetData,
    handleAddMoreClaim,
    handleClearClaim,
    handleResetClaim,
    handleRemoveClaim,
    handlePersonInCharge,
    handleClearTimesheet,
    isChargeAbleAmtValid,
    handleSubmitTimesheet,
    handleSaveTimesheet,
    handleAddMore
  } = useTimesheetfns({
    personIncharge,
    OfficeSuperVisorUserId,
    currentWeek,
    form,
    setPersonInCharge,
    week,
    timesheetDataRes,
    onSubmit,
    isFetched
  });
  const noTimesheet = !timesheetDataRes?.response?.response?.timeSheet;

  const { mutate: update, isLoading: isUpdating } = useUpdate(
    postTimesheet.key,
    postTimesheet.url
  );

  return (
    <Form
      form={form}
      initialValues={timeSheet}
      layout="vertical"
      onFinishFailed={() => {
        notification.error({
          message: "Please fill all the required fields",
        });
      }}
    >
      <div className="timesheet-header">
        <div>
          <img
            src={LeftIcon}
            alt="left"
            className="left-icon"
            onClick={handlePreviousWeek}
          />
          <img
            src={RightIcon}
            alt="right"
            className="right-icon"
            onClick={handleNextWeek}
          />
          <span className="d-inline-flex justify-content-center align-items-center gap-2">
            <span className="date">Date:</span>{" "}
            {edit ? (
              <ClickAwayListener onClickAway={() => setEdit(false)}>
                <div className="position-relative">
                  <ConfigProvider locale={en_GB}>
                    <DatePicker
                      onChange={onChange}
                      picker="week"
                      value={date}
                      open={true}
                      disabledDate={disabledDate}
                    />
                  </ConfigProvider>
                </div>
              </ClickAwayListener>
            ) : (
              <div className="current-week" onClick={() => setEdit(true)}>
                {currentNiceWeekRange}
              </div>
            )}
          </span>
        </div>

        {editable ? (
          <></>
        ) : (
          !isLoading &&
          !noTimesheet && (
            <IconButton
              onClick={() => {
                notification.info({
                  message: "now you can edit the timesheet",
                });
                setEditable(!editable);
                setEditables(true);
              }}
            >
              <EditIcon  />
            </IconButton>
          )
        )}
      </div>

      <Spin spinning={isLoading}>
        {!isLoading && noTimesheet ? (
          <div className="no-timesheet">
            No Declined Timesheet Found for this Week
          </div>
        ) : (
          <>
            <div className="mb-4">
              <TimeSheetOverView timeSheet={timeSheet} />
            </div>

            <TimeSheet
              editable={editable}
              editables={editables}
              data={timeSheet}
              extraActions={{
                handlePersonInCharge,
                handleClearTimesheet,
                handleAddMoreClaim,
                handleClearClaim,
                handleResetClaim,
                handleRemoveClaim,
                handleSaveTimesheet
              }}
            />
              {editable && !isLoading && (
             <div className="d-flex justify-content-start align-items-center gap-2 mt-4">
                <Button type="primary" className="add-more" onClick={handleAddMore}>
                  Add More
                </Button>
              </div>
            )}
            <TimeSheetFooterHint />

            <div className="d-flex justify-content-start align-items-center gap-2 mt-4">
              {editable && !isLoading && (
                <Button
                  type="primary"
                  onClick={handleSubmitTimesheet}
                  loading={isUpdating}
                  className="submit-button"
                >
                  Submit
                </Button>
              )}
            </div>
          </>
        )}
      </Spin>
    </Form>
  );
};

export default DeclinedTimeSheet;
